/**
 * Notification Service
 *
 * Provides a notification API that can be used outside React components (e.g., Redux reducers).
 * This is a singleton service that queues notifications and dispatches them when the app is ready.
 *
 * @see .github/copilot-instructions.md#api--services Service patterns
 *
 * Usage in Redux reducers:
 * ```typescript
 * import { notificationService } from "services/notificationService";
 *
 * notificationService.success("Operation completed!");
 * notificationService.error("Failed to save data");
 * ```
 */

import { VariantType } from 'notistack';

export interface NotificationMessage {
  message: string;
  variant: VariantType;
  autoHideDuration?: number;
}

type EnqueueSnackbarFn = (
  message: string,
  options: { variant: VariantType; autoHideDuration?: number },
) => void;

class NotificationService {
  private enqueueSnackbar: EnqueueSnackbarFn | null = null;
  private queue: NotificationMessage[] = [];

  /**
   * Register the enqueueSnackbar function from notistack.
   * This should be called once when the app initializes.
   */
  register(enqueueSnackbar: EnqueueSnackbarFn) {
    this.enqueueSnackbar = enqueueSnackbar;
    // Process queued notifications
    this.queue.forEach((notification) => {
      this.show(notification);
    });
    this.queue = [];
  }

  /**
   * Show a notification. If the service is not registered yet, queue it.
   */
  private show(notification: NotificationMessage) {
    if (this.enqueueSnackbar) {
      this.enqueueSnackbar(notification.message, {
        variant: notification.variant,
        autoHideDuration: notification.autoHideDuration,
      });
    } else {
      // Queue for later if service not ready
      this.queue.push(notification);
    }
  }

  success(message: string, autoHideDuration?: number) {
    this.show({ message, variant: 'success', autoHideDuration });
  }

  error(message: string, autoHideDuration?: number) {
    this.show({ message, variant: 'error', autoHideDuration });
  }

  warning(message: string, autoHideDuration?: number) {
    this.show({ message, variant: 'warning', autoHideDuration });
  }

  info(message: string, autoHideDuration?: number) {
    this.show({ message, variant: 'info', autoHideDuration });
  }

  default(message: string, autoHideDuration?: number) {
    this.show({ message, variant: 'default', autoHideDuration });
  }
}

export const notificationService = new NotificationService();
